// based on: https://github.com/facebook/lexical/blob/main/packages/lexical-playground/src/plugins/ToolbarPlugin/utils.ts
import { DEFAULT_FONT_SIZE, MAX_ALLOWED_FONT_SIZE, MIN_ALLOWED_FONT_SIZE } from "../_plugins/ToolbarPlugin/_components/FontSize.lexical.component";
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from "@lexical/list";
import { $isDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import { $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import { $patchStyleText, $setBlocksType } from "@lexical/selection";
import { $isTableSelection } from "@lexical/table";
import { $getNearestBlockElementAncestorOrThrow } from "@lexical/utils";
import { $createParagraphNode, $getSelection, $isRangeSelection, $isTextNode } from "lexical";
export var UpdateFontSizeType;
(function (UpdateFontSizeType) {
    UpdateFontSizeType[UpdateFontSizeType["increment"] = 1] = "increment";
    UpdateFontSizeType[UpdateFontSizeType["decrement"] = 2] = "decrement";
})(UpdateFontSizeType || (UpdateFontSizeType = {}));
/**
 * Calculates the new font size based on the update type.
 * @param currentFontSize - The current font size
 * @param updateType - The type of change, either increment or decrement
 * @returns the next font size
 */
export const calculateNextFontSize = (currentFontSize, updateType) => {
    if (!updateType) {
        return currentFontSize;
    }
    let updatedFontSize = currentFontSize;
    switch (updateType) {
        case UpdateFontSizeType.decrement:
            switch (true) {
                case currentFontSize > MAX_ALLOWED_FONT_SIZE:
                    updatedFontSize = MAX_ALLOWED_FONT_SIZE;
                    break;
                case currentFontSize >= 48:
                    updatedFontSize -= 12;
                    break;
                case currentFontSize >= 26:
                    updatedFontSize -= 2;
                    break;
                case currentFontSize >= 14:
                    updatedFontSize -= 1;
                    break;
                default:
                    updatedFontSize = MIN_ALLOWED_FONT_SIZE;
                    break;
            }
            break;
        case UpdateFontSizeType.increment:
            switch (true) {
                case currentFontSize < MIN_ALLOWED_FONT_SIZE:
                    updatedFontSize = MIN_ALLOWED_FONT_SIZE;
                    break;
                case currentFontSize < 24:
                    updatedFontSize += 1;
                    break;
                case currentFontSize < 36:
                    updatedFontSize += 2;
                    break;
                case currentFontSize <= 60:
                    updatedFontSize += 12;
                    break;
                default:
                    updatedFontSize = MAX_ALLOWED_FONT_SIZE;
                    break;
            }
            break;
        default:
            break;
    }
    return updatedFontSize;
};
/**
 * Patches the selection with the updated font size.
 */
export const updateFontSizeInSelection = (editor, newFontSize, updateType) => {
    const getNextFontSize = (prevFontSize) => {
        if (!prevFontSize) {
            prevFontSize = `${DEFAULT_FONT_SIZE}px`;
        }
        prevFontSize = prevFontSize.slice(0, -2);
        const nextFontSize = calculateNextFontSize(Number(prevFontSize), updateType);
        return `${nextFontSize}px`;
    };
    editor.update(() => {
        if (editor.isEditable()) {
            const selection = $getSelection();
            if (selection !== null) {
                $patchStyleText(selection, {
                    "font-size": newFontSize || getNextFontSize
                });
            }
        }
    });
};
export const updateFontSize = (editor, updateType, inputValue) => {
    if (inputValue !== "") {
        const nextFontSize = calculateNextFontSize(Number(inputValue), updateType);
        updateFontSizeInSelection(editor, String(nextFontSize) + "px", null);
    }
    else {
        updateFontSizeInSelection(editor, null, updateType);
    }
};
export const formatParagraph = (editor) => {
    editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createParagraphNode());
        }
    });
};
export const formatBulletList = (editor, blockType) => {
    if (blockType !== "bullet") {
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
    else {
        formatParagraph(editor);
    }
};
export const formatNumberedList = (editor, blockType) => {
    if (blockType !== "number") {
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
    else {
        formatParagraph(editor);
    }
};
export const clearFormatting = (editor) => {
    editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || $isTableSelection(selection)) {
            const anchor = selection.anchor;
            const focus = selection.focus;
            const nodes = selection.getNodes();
            const extractedNodes = selection.extract();
            if (anchor.key === focus.key && anchor.offset === focus.offset) {
                return;
            }
            nodes.forEach((node, idx) => {
                // We split the first and last node by the selection
                // So that we don't format unselected text inside those nodes
                if ($isTextNode(node)) {
                    // Use a separate variable to ensure TS does not lose the refinement
                    let textNode = node;
                    if (idx === 0 && anchor.offset !== 0) {
                        textNode = textNode.splitText(anchor.offset)[1] || textNode;
                    }
                    if (idx === nodes.length - 1) {
                        textNode = textNode.splitText(focus.offset)[0] || textNode;
                    }
                    /**
                     * If the selected text has one format applied
                     * selecting a portion of the text, could
                     * clear the format to the wrong portion of the text.
                     *
                     * The cleared text is based on the length of the selected text.
                     */
                    // We need this in case the selected text only has one format
                    const extractedTextNode = extractedNodes[0];
                    if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
                        textNode = extractedTextNode;
                    }
                    if (textNode.__style !== "") {
                        textNode.setStyle("");
                    }
                    if (textNode.__format !== 0) {
                        textNode.setFormat(0);
                        $getNearestBlockElementAncestorOrThrow(textNode).setFormat("");
                    }
                    node = textNode;
                }
                else if ($isHeadingNode(node) || $isQuoteNode(node)) {
                    node.replace($createParagraphNode(), true);
                }
                else if ($isDecoratorBlockNode(node)) {
                    node.setFormat("");
                }
            });
        }
    });
};
